import React, { useEffect, useState } from "react";
import { ViewContainer } from "./components/viewContainer";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { theme } from "../theme";
import {
  useListSettingsQuery,
  useUpdateSettingsMutation,
} from "../apis/settingsAPI";
import { ISetting } from "@backend/model/setting";
import { getOrganisation } from "../slices/organisationSlice";
import { selector } from "../store";
import { isSettingEnabled } from "../utils/settingUtils";

const SettingsRow = (props: {
  label: string;
  value: boolean;
  onValueChanged: (val: boolean) => void;
}) => {
  return (
    <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
      <Checkbox
        checked={props.value}
        onChange={() => props.onValueChanged(!props.value)}
        style={{ marginRight: theme.spacing(1) }}
      />
      <Typography variant="body1">{props.label}</Typography>
    </Box>
  );
};

export const SettingsView = () => {
  const { t } = useTranslation();

  // Employee
  const [isTaxNumberVisible, setIsTaxNumberVisible] = useState(false);
  const [isPhoneVisible, setIsPhoneVisible] = useState(false);
  const [isCompanyVisible, setIsCompanyVisible] = useState(false);
  const [isSafetyCardVisible, setIsSafetyCardVisible] = useState(false);
  const [isHotWorkCardVisible, setIsHotWorkCardVisible] = useState(false);
  const [isDateOfBirthVisible, setIsDateOfBirthVisible] = useState(false);
  const [isSafetyCardImageVisible, setIsSafetyCardImageVisible] =
    useState(false);
  const [isFirstAidCardImageVisible, setIsFirstAidCardImageVisible] =
    useState(false);
  const [isHotWorkCardImageVisible, setIsHotWorkCardImageVisible] =
    useState(false);
  const [isShoeSizeVisible, setIsShoeSizeVisible] = useState(false);
  const [isJacketSizeVisible, setIsJacketSizeVisible] = useState(false);
  const [isDietaryRestrictionsVisible, setIsDietaryRestrictionsVisible] =
    useState(false);
  const [isVisitationDateVisible, setIsVisitationDateVisible] = useState(false);

  // Visitor
  const [isVisitorTaxNumberVisible, setIsVisitorTaxNumberVisible] =
    useState(false);
  const [isVisitorPhoneVisible, setIsVisitorPhoneVisible] = useState(false);
  const [isVisitorCompanyVisible, setIsVisitorCompanyVisible] = useState(false);
  const [isVisitorSafetyCardVisible, setIsVisitorSafetyCardVisible] =
    useState(false);
  const [isVisitorHotWorkCardVisible, setIsVisitorHotWorkCardVisible] =
    useState(false);
  const [isVisitorDateOfBirthVisible, setIsVisitorDateOfBirthVisible] =
    useState(false);
  const [isVisitorSafetyCardImageVisible, setIsVisitorSafetyCardImageVisible] =
    useState(false);
  const [
    isVisitorFirstAidCardImageVisible,
    setIsVisitorFirstAidCardImageVisible,
  ] = useState(false);
  const [
    isVisitorHotWorkCardImageVisible,
    setIsVisitorHotWorkCardImageVisible,
  ] = useState(false);
  const [isVisitorShoeSizeVisible, setIsVisitorShoeSizeVisible] =
    useState(false);
  const [isVisitorJacketSizeVisible, setIsVisitorJacketSizeVisible] =
    useState(false);
  const [
    isVisitorDietaryRestrictionsVisible,
    setIsVisitorDietaryRestrictionsVisible,
  ] = useState(false);
  const [isVisitorVisitationDateVisible, setIsVisitorVisitationDateVisible] =
    useState(false);

  // Contractor
  const [isContractorTaxNumberVisible, setIsContractorTaxNumberVisible] =
    useState(false);
  const [isContractorPhoneVisible, setIsContractorPhoneVisible] =
    useState(false);
  const [isContractorCompanyVisible, setIsContractorCompanyVisible] =
    useState(false);
  const [isContractorSafetyCardVisible, setIsContractorSafetyCardVisible] =
    useState(false);
  const [isContractorHotWorkCardVisible, setIsContractorHotWorkCardVisible] =
    useState(false);
  const [isContractorDateOfBirthVisible, setIsContractorDateOfBirthVisible] =
    useState(false);
  const [
    isContractorSafetyCardImageVisible,
    setIsContractorSafetyCardImageVisible,
  ] = useState(false);
  const [
    isContractorFirstAidCardImageVisible,
    setIsContractorFirstAidCardImageVisible,
  ] = useState(false);
  const [
    isContractorHotWorkCardImageVisible,
    setIsContractorHotWorkCardImageVisible,
  ] = useState(false);
  const [isContractorShoeSizeVisible, setIsContractorShoeSizeVisible] =
    useState(false);
  const [isContractorJacketSizeVisible, setIsContractorJacketSizeVisible] =
    useState(false);
  const [
    isContractorDietaryRestrictionsVisible,
    setIsContractorDietaryRestrictionsVisible,
  ] = useState(false);
  const [
    isContractorVisitationDateVisible,
    setIsContractorVisitationDateVisible,
  ] = useState(false);

  const [saveSettings, { isLoading: saving }] = useUpdateSettingsMutation();
  const org = selector((state) => getOrganisation(state));
  const { data: settings, isFetching: loadingSettings } = useListSettingsQuery({
    organisationId: org?.organisationId || "",
  });

  useEffect(() => {
    if (!loadingSettings && settings) {
      // Employee
      setIsTaxNumberVisible(isSettingEnabled(settings, "taxnumberVisible"));
      setIsPhoneVisible(isSettingEnabled(settings, "phoneVisible"));
      setIsCompanyVisible(isSettingEnabled(settings, "companyVisible"));
      setIsSafetyCardVisible(
        isSettingEnabled(settings, "occupationalSafetyCardVisible")
      );
      setIsHotWorkCardVisible(isSettingEnabled(settings, "hotWorkCardVisible"));
      setIsDateOfBirthVisible(isSettingEnabled(settings, "dateOfBirthVisible"));
      setIsSafetyCardImageVisible(
        isSettingEnabled(settings, "occupationalSafetyCardImageVisible")
      );
      setIsFirstAidCardImageVisible(
        isSettingEnabled(settings, "firstAidCardImageVisible")
      );
      setIsHotWorkCardImageVisible(
        isSettingEnabled(settings, "hotWorkCardImageVisible")
      );
      setIsShoeSizeVisible(isSettingEnabled(settings, "shoeSizeVisible"));
      setIsJacketSizeVisible(isSettingEnabled(settings, "jacketSizeVisible"));
      setIsDietaryRestrictionsVisible(
        isSettingEnabled(settings, "dietaryRestrictionsVisible")
      );
      setIsVisitationDateVisible(
        isSettingEnabled(settings, "visitationDateVisible")
      );

      // Visitor
      setIsVisitorTaxNumberVisible(
        isSettingEnabled(settings, "visitorTaxnumberVisible")
      );
      setIsVisitorPhoneVisible(
        isSettingEnabled(settings, "visitorPhoneVisible")
      );
      setIsVisitorCompanyVisible(
        isSettingEnabled(settings, "visitorCompanyVisible")
      );
      setIsVisitorSafetyCardVisible(
        isSettingEnabled(settings, "visitorOccupationalSafetyCardVisible")
      );
      setIsVisitorHotWorkCardVisible(
        isSettingEnabled(settings, "visitorHotWorkCardVisible")
      );
      setIsVisitorDateOfBirthVisible(
        isSettingEnabled(settings, "visitorDateOfBirthVisible")
      );
      setIsVisitorSafetyCardImageVisible(
        isSettingEnabled(settings, "visitorOccupationalSafetyCardImageVisible")
      );
      setIsVisitorFirstAidCardImageVisible(
        isSettingEnabled(settings, "visitorFirstAidCardImageVisible")
      );
      setIsVisitorHotWorkCardImageVisible(
        isSettingEnabled(settings, "visitorHotWorkCardImageVisible")
      );
      setIsVisitorShoeSizeVisible(
        isSettingEnabled(settings, "visitorShoeSizeVisible")
      );
      setIsVisitorJacketSizeVisible(
        isSettingEnabled(settings, "visitorJacketSizeVisible")
      );
      setIsVisitorDietaryRestrictionsVisible(
        isSettingEnabled(settings, "visitorDietaryRestrictionsVisible")
      );
      setIsVisitorVisitationDateVisible(
        isSettingEnabled(settings, "visitorVisitationDateVisible")
      );

      // Contractor
      setIsContractorTaxNumberVisible(
        isSettingEnabled(settings, "contractorTaxnumberVisible")
      );
      setIsContractorPhoneVisible(
        isSettingEnabled(settings, "contractorPhoneVisible")
      );
      setIsContractorCompanyVisible(
        isSettingEnabled(settings, "contractorCompanyVisible")
      );
      setIsContractorSafetyCardVisible(
        isSettingEnabled(settings, "contractorOccupationalSafetyCardVisible")
      );
      setIsContractorHotWorkCardVisible(
        isSettingEnabled(settings, "contractorHotWorkCardVisible")
      );
      setIsContractorDateOfBirthVisible(
        isSettingEnabled(settings, "contractorDateOfBirthVisible")
      );
      setIsContractorSafetyCardImageVisible(
        isSettingEnabled(
          settings,
          "contractorOccupationalSafetyCardImageVisible"
        )
      );
      setIsContractorFirstAidCardImageVisible(
        isSettingEnabled(settings, "contractorFirstAidCardImageVisible")
      );
      setIsContractorHotWorkCardImageVisible(
        isSettingEnabled(settings, "contractorHotWorkCardImageVisible")
      );
      setIsContractorShoeSizeVisible(
        isSettingEnabled(settings, "contractorShoeSizeVisible")
      );
      setIsContractorJacketSizeVisible(
        isSettingEnabled(settings, "contractorJacketSizeVisible")
      );
      setIsContractorDietaryRestrictionsVisible(
        isSettingEnabled(settings, "contractorDietaryRestrictionsVisible")
      );
      setIsContractorVisitationDateVisible(
        isSettingEnabled(settings, "contractorVisitationDateVisible")
      );
    }
  }, [settings, loadingSettings]);

  return (
    <ViewContainer loading={saving}>
      <Typography variant="h4">{t("settings")}</Typography>
      <Divider
        style={{
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(2),
        }}
      />
      <Typography variant="h6">{t("peopleInfo")}</Typography>
      <Divider
        style={{
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(2),
        }}
      />
      <Grid container spacing={2} sx={{ padding: theme.spacing(2) }}>
        <Grid>
          <Typography>{t("employee")}</Typography>
          <SettingsRow
            label={t("isTaxNumberVisible")}
            onValueChanged={setIsTaxNumberVisible}
            value={isTaxNumberVisible}
          />
          <SettingsRow
            label={t("isPhoneVisible")}
            onValueChanged={setIsPhoneVisible}
            value={isPhoneVisible}
          />
          <SettingsRow
            label={t("isCompanyVisible")}
            onValueChanged={setIsCompanyVisible}
            value={isCompanyVisible}
          />
          <SettingsRow
            label={t("isSafetyCardVisible")}
            onValueChanged={setIsSafetyCardVisible}
            value={isSafetyCardVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardVisible")}
            onValueChanged={setIsHotWorkCardVisible}
            value={isHotWorkCardVisible}
          />
          <SettingsRow
            label={t("isDateOfBirthVisible")}
            onValueChanged={setIsDateOfBirthVisible}
            value={isDateOfBirthVisible}
          />
          <SettingsRow
            label={t("isSafetyCardImageVisible")}
            onValueChanged={setIsSafetyCardImageVisible}
            value={isSafetyCardImageVisible}
          />
          <SettingsRow
            label={t("isFirstAidCardVisible")}
            onValueChanged={setIsFirstAidCardImageVisible}
            value={isFirstAidCardImageVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardImageVisible")}
            onValueChanged={setIsHotWorkCardImageVisible}
            value={isHotWorkCardImageVisible}
          />
          <SettingsRow
            label={t("isShoeSizeVisible")}
            onValueChanged={setIsShoeSizeVisible}
            value={isShoeSizeVisible}
          />
          <SettingsRow
            label={t("isJacketSizeVisible")}
            onValueChanged={setIsJacketSizeVisible}
            value={isJacketSizeVisible}
          />
          <SettingsRow
            label={t("isDietaryRestrictionsVisible")}
            onValueChanged={setIsDietaryRestrictionsVisible}
            value={isDietaryRestrictionsVisible}
          />
          <SettingsRow
            label={t("isVisitationDateVisible")}
            onValueChanged={setIsVisitationDateVisible}
            value={isVisitationDateVisible}
          />
        </Grid>

        <Grid>
          <Typography>{t("contractor")}</Typography>
          <SettingsRow
            label={t("isTaxNumberVisible")}
            onValueChanged={setIsContractorTaxNumberVisible}
            value={isContractorTaxNumberVisible}
          />
          <SettingsRow
            label={t("isPhoneVisible")}
            onValueChanged={setIsContractorPhoneVisible}
            value={isContractorPhoneVisible}
          />
          <SettingsRow
            label={t("isCompanyVisible")}
            onValueChanged={setIsContractorCompanyVisible}
            value={isContractorCompanyVisible}
          />
          <SettingsRow
            label={t("isSafetyCardVisible")}
            onValueChanged={setIsContractorSafetyCardVisible}
            value={isContractorSafetyCardVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardVisible")}
            onValueChanged={setIsContractorHotWorkCardVisible}
            value={isContractorHotWorkCardVisible}
          />
          <SettingsRow
            label={t("isDateOfBirthVisible")}
            onValueChanged={setIsContractorDateOfBirthVisible}
            value={isContractorDateOfBirthVisible}
          />
          <SettingsRow
            label={t("isSafetyCardImageVisible")}
            onValueChanged={setIsContractorSafetyCardImageVisible}
            value={isContractorSafetyCardImageVisible}
          />
          <SettingsRow
            label={t("isFirstAidCardVisible")}
            onValueChanged={setIsContractorFirstAidCardImageVisible}
            value={isContractorFirstAidCardImageVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardImageVisible")}
            onValueChanged={setIsContractorHotWorkCardImageVisible}
            value={isContractorHotWorkCardImageVisible}
          />
          <SettingsRow
            label={t("isShoeSizeVisible")}
            onValueChanged={setIsContractorShoeSizeVisible}
            value={isContractorShoeSizeVisible}
          />
          <SettingsRow
            label={t("isJacketSizeVisible")}
            onValueChanged={setIsContractorJacketSizeVisible}
            value={isContractorJacketSizeVisible}
          />
          <SettingsRow
            label={t("isDietaryRestrictionsVisible")}
            onValueChanged={setIsContractorDietaryRestrictionsVisible}
            value={isContractorDietaryRestrictionsVisible}
          />
          <SettingsRow
            label={t("isVisitationDateVisible")}
            onValueChanged={setIsContractorVisitationDateVisible}
            value={isContractorVisitationDateVisible}
          />
        </Grid>

        <Grid>
          <Typography>{t("visitor")}</Typography>
          <SettingsRow
            label={t("isTaxNumberVisible")}
            onValueChanged={setIsVisitorTaxNumberVisible}
            value={isVisitorTaxNumberVisible}
          />
          <SettingsRow
            label={t("isPhoneVisible")}
            onValueChanged={setIsVisitorPhoneVisible}
            value={isVisitorPhoneVisible}
          />
          <SettingsRow
            label={t("isCompanyVisible")}
            onValueChanged={setIsVisitorCompanyVisible}
            value={isVisitorCompanyVisible}
          />
          <SettingsRow
            label={t("isSafetyCardVisible")}
            onValueChanged={setIsVisitorSafetyCardVisible}
            value={isVisitorSafetyCardVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardVisible")}
            onValueChanged={setIsVisitorHotWorkCardVisible}
            value={isVisitorHotWorkCardVisible}
          />
          <SettingsRow
            label={t("isDateOfBirthVisible")}
            onValueChanged={setIsVisitorDateOfBirthVisible}
            value={isVisitorDateOfBirthVisible}
          />
          <SettingsRow
            label={t("isSafetyCardImageVisible")}
            onValueChanged={setIsVisitorSafetyCardImageVisible}
            value={isVisitorSafetyCardImageVisible}
          />
          <SettingsRow
            label={t("isFirstAidCardVisible")}
            onValueChanged={setIsVisitorFirstAidCardImageVisible}
            value={isVisitorFirstAidCardImageVisible}
          />
          <SettingsRow
            label={t("isHotWorkCardImageVisible")}
            onValueChanged={setIsVisitorHotWorkCardImageVisible}
            value={isVisitorHotWorkCardImageVisible}
          />
          <SettingsRow
            label={t("isShoeSizeVisible")}
            onValueChanged={setIsVisitorShoeSizeVisible}
            value={isVisitorShoeSizeVisible}
          />
          <SettingsRow
            label={t("isJacketSizeVisible")}
            onValueChanged={setIsVisitorJacketSizeVisible}
            value={isVisitorJacketSizeVisible}
          />
          <SettingsRow
            label={t("isDietaryRestrictionsVisible")}
            onValueChanged={setIsVisitorDietaryRestrictionsVisible}
            value={isVisitorDietaryRestrictionsVisible}
          />
          <SettingsRow
            label={t("isVisitationDateVisible")}
            onValueChanged={setIsVisitorVisitationDateVisible}
            value={isVisitorVisitationDateVisible}
          />
        </Grid>
      </Grid>
      <Divider
        style={{
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(2),
        }}
      />
      <Button
        variant="contained"
        onClick={async () => {
          saveSettings({
            settings: [
              // Employee
              { type: "taxnumberVisible", value: isTaxNumberVisible },
              { type: "phoneVisible", value: isPhoneVisible },
              { type: "companyVisible", value: isCompanyVisible },
              {
                type: "occupationalSafetyCardVisible",
                value: isSafetyCardVisible,
              },
              {
                type: "hotWorkCardVisible",
                value: isHotWorkCardVisible,
              },
              { type: "dateOfBirthVisible", value: isDateOfBirthVisible },
              {
                type: "occupationalSafetyCardImageVisible",
                value: isSafetyCardImageVisible,
              },
              {
                type: "firstAidCardImageVisible",
                value: isFirstAidCardImageVisible,
              },
              {
                type: "hotWorkCardImageVisible",
                value: isHotWorkCardImageVisible,
              },
              {
                type: "shoeSizeVisible",
                value: isShoeSizeVisible,
              },
              {
                type: "jacketSizeVisible",
                value: isJacketSizeVisible,
              },
              {
                type: "dietaryRestrictionsVisible",
                value: isDietaryRestrictionsVisible,
              },
              {
                type: "visitationDateVisible",
                value: isVisitationDateVisible,
              },
              // Contractor
              {
                type: "contractorTaxnumberVisible",
                value: isContractorTaxNumberVisible,
              },
              {
                type: "contractorPhoneVisible",
                value: isContractorPhoneVisible,
              },
              {
                type: "contractorCompanyVisible",
                value: isContractorCompanyVisible,
              },
              {
                type: "contractorOccupationalSafetyCardVisible",
                value: isContractorSafetyCardVisible,
              },
              {
                type: "contractorHotWorkCardVisible",
                value: isContractorHotWorkCardVisible,
              },
              {
                type: "contractorDateOfBirthVisible",
                value: isContractorDateOfBirthVisible,
              },
              {
                type: "contractorOccupationalSafetyCardImageVisible",
                value: isContractorSafetyCardImageVisible,
              },
              {
                type: "contractorFirstAidCardImageVisible",
                value: isContractorFirstAidCardImageVisible,
              },
              {
                type: "contractorHotWorkCardImageVisible",
                value: isContractorHotWorkCardImageVisible,
              },
              {
                type: "contractorShoeSizeVisible",
                value: isContractorShoeSizeVisible,
              },
              {
                type: "contractorJacketSizeVisible",
                value: isContractorJacketSizeVisible,
              },
              {
                type: "contractorDietaryRestrictionsVisible",
                value: isContractorDietaryRestrictionsVisible,
              },
              {
                type: "contractorVisitationDateVisible",
                value: isContractorVisitationDateVisible,
              },
              // Visitor
              {
                type: "visitorTaxnumberVisible",
                value: isVisitorTaxNumberVisible,
              },
              { type: "visitorPhoneVisible", value: isVisitorPhoneVisible },
              { type: "visitorCompanyVisible", value: isVisitorCompanyVisible },
              {
                type: "visitorOccupationalSafetyCardVisible",
                value: isVisitorSafetyCardVisible,
              },
              {
                type: "visitorHotWorkCardVisible",
                value: isVisitorHotWorkCardVisible,
              },
              {
                type: "visitorDateOfBirthVisible",
                value: isVisitorDateOfBirthVisible,
              },
              {
                type: "visitorOccupationalSafetyCardImageVisible",
                value: isVisitorSafetyCardImageVisible,
              },
              {
                type: "visitorFirstAidCardImageVisible",
                value: isVisitorFirstAidCardImageVisible,
              },
              {
                type: "visitorHotWorkCardImageVisible",
                value: isVisitorHotWorkCardImageVisible,
              },
              {
                type: "visitorShoeSizeVisible",
                value: isVisitorShoeSizeVisible,
              },
              {
                type: "visitorJacketSizeVisible",
                value: isVisitorJacketSizeVisible,
              },
              {
                type: "visitorDietaryRestrictionsVisible",
                value: isVisitorDietaryRestrictionsVisible,
              },
              {
                type: "visitorVisitationDateVisible",
                value: isVisitorVisitationDateVisible,
              },
            ] as ISetting[],
            organisationId: org?.organisationId || "",
          });
        }}
      >
        {t("save")}
      </Button>
    </ViewContainer>
  );
};
